import { FC, useMemo, useState } from 'react';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components/UI';
import {
  ActionAttachmentMenuGlobalStyles,
  MenuDropdown,
  MenuItemStyled,
} from './styles';
import Tooltip from 'Components/UI/Tooltip';
import AccessPermission from 'Utils/AcessPermission';

interface IActionAttachmentDropdown {
  hasDeletePermission?: boolean;
  renameFlagEnabled?: boolean;
  isPreview?: boolean;
  onDownload?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onOpenPage: () => void;
}

const ActionAttachmentDropdown: FC<IActionAttachmentDropdown> = ({
  children,
  hasDeletePermission,
  renameFlagEnabled,
  isPreview,
  onDownload,
  onRename,
  onDelete,
  onOpenPage,
}) => {
  const { t } = useTranslation('activities');
  const [isActive, setIsActive] = useState(false);

  const items = useMemo(() => {
    const defalt: MenuProps['items'] = [];

    if (!isPreview && onDownload) {
      defalt.push({
        label: (
          <MenuItemStyled onClick={onDownload}>
            <Icon
              icon="download"
              customSize={14}
              className="iconMenuItem"
            />
            <span className="textItem">
              {t('attachmentsView.download')}
            </span>
          </MenuItemStyled>
        ),
        key: '0',
        className: 'rowMenu',
      });

      if (renameFlagEnabled) {
        defalt.push({
          label: (
            <MenuItemStyled onClick={onRename}>
              <Icon
                icon="editar"
                customSize={14}
                className="iconMenuItem"
              />
              <span className="textItem">
                {t('attachmentsView.rename')}
              </span>
            </MenuItemStyled>
          ),
          key: '2',
          className: 'rowMenu',
        });
      }

      if (!AccessPermission.isConsultor() && onDelete) {
        defalt.push({
          label: (
            <Tooltip
              placement="top"
              overlay={
                hasDeletePermission
                  ? undefined
                  : t('attachmentsView.deletePermissionTooltip')
              }
              zIndex={1099}
            >
              <MenuItemStyled onClick={onDelete} isdisabled={`${!hasDeletePermission}`}>
                <Icon
                  icon="excluir"
                  customSize={14}
                  className="iconMenuItem"
                />
                <span className="textItem">
                  {t('attachmentsView.delete')}
                </span>
              </MenuItemStyled>
            </Tooltip>
          ),
          key: '1',
          className: 'rowMenu',
        });
      }

      defalt.push({
        type: 'divider',
      });
    }

    defalt.push(
      {
        label: (
          <MenuItemStyled onClick={onOpenPage}>
            <Icon
              icon="openNewTab"
              customSize={14}
              className="iconMenuItem"
            />
            <span className="textItem">
              {t('attachmentsView.openInNewTab')}
            </span>
          </MenuItemStyled>
        ),
        key: '3',
        className: 'rowMenu',
      }
    );

    return defalt;
  }, [
    hasDeletePermission,
    isPreview,
    renameFlagEnabled,
    onDownload,
    onRename,
    onDelete,
    onOpenPage,
    t,
  ]);

  return (
    <>
      <ActionAttachmentMenuGlobalStyles />
      <MenuDropdown
        trigger={["click"]}
        className="ActionAttachmentDropdown"
        onOpenChange={(open) => setIsActive(open)}
        menu={{ items }}
        isactive={`${isActive}`}
      >
        {children}
      </MenuDropdown>
    </>
  )
};

export default ActionAttachmentDropdown;
